if (!customElements.get('gallery-zoom-open')) {
  /**
   * gallery-zoom.css en modal.css hangen in de pagina als media="print" met een onload die
   * ze omzet naar media="all". Dat houdt ze buiten het eerste renderen, maar het betekent
   * ook dat er een venster is waarin de opmaak er nog NIET is. Op een telefoon met een traag
   * netwerk is dat venster ruim genoeg om er al op te tikken, en dan opent het venster zonder
   * opmaak: de teller valt uit de kopbalk en de foto staat op ware grootte half buiten beeld.
   *
   * Dit script is defer, dus het draait pas na het parsen van de HTML. Hier omschakelen kost
   * dus geen renderblokkade, maar sluit dat venster wel.
   */
  const activeerZoomStijlbladen = () => {
    document.querySelectorAll('link[rel="stylesheet"][media="print"]').forEach((link) => {
      if (/(gallery-zoom|modal)\.css/.test(link.href)) link.media = 'all';
    });
  };
  activeerZoomStijlbladen();

  class GalleryZoomOpen extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * Handle a click event.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const mediaGallery = this.closest('media-gallery');
      const zoomDevice = mediaGallery.dataset.zoomEnabled;
      activeerZoomStijlbladen();

      if (zoomDevice.length === 0 || (zoomDevice === 'mobile' && !theme.mediaMatches.md) || (zoomDevice === 'desktop' && theme.mediaMatches.md)) {
        // Add to document on first open
        if (!mediaGallery.galleryModal.parentElement) {
          document.body.appendChild(mediaGallery.galleryModal);
        }

        // De opener moet een focusbaar element zijn, anders komt de focus na sluiten
        // op <body> terecht in plaats van terug op de foto.
        mediaGallery.galleryModal.open(evt.currentTarget.querySelector('.js-zoom-link') || evt.currentTarget);
        const zoom = mediaGallery.galleryModal.querySelector('gallery-zoom');
        zoom.init(evt.currentTarget.parentElement.dataset.mediaId);
        // Focus op de sluitknop: dat is de laatste focusbare knoop in het venster, waardoor
        // de focustrap meteen gewapend is. Met focus op de container zelf loopt Shift+Tab
        // er direct uit.
        (zoom.querySelector('.gallery-zoom__close') || zoom).focus();

        evt.preventDefault();
      }
    }
  }

  window.customElements.define('gallery-zoom-open', GalleryZoomOpen);
}

/* eslint-disable max-len */

if (!customElements.get('gallery-zoom')) {
  class GalleryZoom extends HTMLElement {
    connectedCallback() {
      if (!this.initialised) {
        this.initialised = true;

        // ui
        this.classList.add('gallery-zoom--pre-reveal');
        this.zoomContainer = this.querySelector('.gallery-zoom__zoom-container');
        this.thumbContainer = this.querySelector('.gallery-zoom__thumbs');
        this.controlsContainer = this.querySelector('.gallery-zoom__controls');
        this.previousBtn = this.querySelector('.gallery-zoom__prev');
        this.nextBtn = this.querySelector('.gallery-zoom__next');
        this.counterCurrent = this.querySelector('.media-ctrl__current-item');
        this.counterTotal = this.querySelector('.media-ctrl__total-items');
        this.mediaGallery = document.querySelector('.cc-main-product media-gallery');
        this.thumbSlider = this.querySelector('.gallery-zoom__thumb-slider');

        // consts
        this.wheelZoomMultiplier = -0.001;
        this.pinchZoomMultiplier = 0.003;
        this.touchPanModifier = 1.0;

        // vars
        this.currentZoomImage = null;
        this.currentTransform = {
          panX: 0,
          panY: 0,
          zoom: 1
        };
        this.pinchTracking = {
          isTracking: false,
          lastPinchDistance: 0
        };
        this.touchTracking = {
          isTracking: false,
          lastTouchX: 0,
          lastTouchY: 0
        };

        // events
        // Wie een verre miniatuur aanwijst of aanraakt, klikt hem meestal ook aan.
        this.querySelectorAll('.gallery-zoom__thumb').forEach((el) => {
          el.addEventListener('pointerenter', () => GalleryZoom.prefetchThumb(el), { once: true });
          el.addEventListener('touchstart', () => GalleryZoom.prefetchThumb(el), { once: true, passive: true });
        });
        this.querySelectorAll('.gallery-zoom__thumb').forEach((el) => {
          el.addEventListener('click', this.onThumbClick.bind(this));
        });
        this.addEventListener('touchend', this.stopTrackingTouch.bind(this));
        this.addEventListener('touchmove', this.trackInputMovement.bind(this));
        this.addEventListener('mousemove', this.trackInputMovement.bind(this));
        this.addEventListener('wheel', this.trackWheel.bind(this));
        // prevent pan while swiping thumbnails
        this.thumbContainer.addEventListener('touchmove', (evt) => evt.stopPropagation());
        this.previousBtn.addEventListener('click', this.selectPreviousThumb.bind(this));
        this.nextBtn.addEventListener('click', this.selectNextThumb.bind(this));
        this.zoomContainer.addEventListener('click', this.onZoomContainerClick.bind(this));
        // De waarnemer vuurt op de telefoon ook als de adresbalk in- of uitschuift. Zonder
        // de maatvergelijking hieronder zette elke veeg de zoom van de klant terug naar passend.
        this.vlakMaat = { b: 0, h: 0 };
        new ResizeObserver(() => this.herpositioneerBijMaatwijziging()).observe(this);
      }

      document.documentElement.classList.add('gallery-zoom-open');
      this.addEventListener('keyup', this.handleKeyup.bind(this));
      setTimeout(() => this.classList.remove('gallery-zoom--pre-reveal'), 10);
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      document.documentElement.classList.remove('gallery-zoom-open');
    }

    /**
     * Helper for creating a DOM element.
     * @param {string} type - Element type.
     * @param {string} className - Class to apply to element.
     * @param {Element} appendTo - Element to add to as a child.
     * @param {string} innerHTML - Inner HTML to apply to element.
     * @returns {Element} - The element created.
     */
    static createEl(type, className, appendTo, innerHTML) {
      const el = document.createElement(type);
      el.className = className;
      if (appendTo) {
        appendTo.insertAdjacentElement('beforeend', el);
      }
      if (innerHTML) {
        el.innerHTML = innerHTML;
      }
      return el;
    }

    init(currentMediaId) {
      // Toggle thumbnail visibility based on the current state of media grouping
      if (this.mediaGallery.dataset.mediaGroupingEnabled) {
        let visibleThumbCount = 0;
        Array.from(this.thumbContainer.children).forEach((thumb) => {
          const mediaGalleryImage = this.mediaGallery.querySelector(`.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`);
          thumb.hidden = mediaGalleryImage?.style.display === 'none';
          if (!thumb.hidden) visibleThumbCount += 1;
        });
        if (this.counterTotal) this.counterTotal.textContent = visibleThumbCount;
      }

      this.selectThumb(
        [...this.thumbContainer.children].find((el) => el.dataset.mediaId === currentMediaId)
         || this.thumbContainer.firstElementChild
      );
    }

    /**
     * Update pan values based on input event coordinates.
     * @param {number} inputX - Mouse/touch input X.
     * @param {number} inputY - Mouse/touch input Y.
     */
    panZoomImageFromCoordinate(inputX, inputY) {
      // do nothing if the image fits, pan if not
      const doPanX = this.currentZoomImage.clientWidth > this.zoomContainer.clientWidth;
      const doPanY = this.currentZoomImage.clientHeight > this.zoomContainer.clientHeight;

      if (doPanX || doPanY) {
        const midX = this.zoomContainer.clientWidth / 2;
        const midY = this.zoomContainer.clientHeight / 2;

        const offsetFromCentreX = inputX - midX;
        const offsetFromCentreY = inputY - midY;

        // the offsetMultipler ensures it can only pan to the edge of the image, no further
        let finalOffsetX = 0;
        let finalOffsetY = 0;

        if (doPanX) {
          const offsetMultiplierX = ((this.currentZoomImage.clientWidth - this.zoomContainer.clientWidth) / 2) / midX;
          finalOffsetX = Math.round(-offsetFromCentreX * offsetMultiplierX);
        }
        if (doPanY) {
          const offsetMultiplierY = ((this.currentZoomImage.clientHeight - this.zoomContainer.clientHeight) / 2) / midY;
          finalOffsetY = Math.round(-offsetFromCentreY * offsetMultiplierY);
        }

        this.currentTransform.panX = finalOffsetX;
        this.currentTransform.panY = finalOffsetY;
        this.alterCurrentPanBy(0, 0); // sanitise
        this.updateImagePosition();
      }
    }

    /**
     * Update pan values by a delta.
     * @param {number} x - Distance to pan X.
     * @param {number} y - Distance to pan Y.
     */
    alterCurrentPanBy(x, y) {
      this.currentTransform.panX += x;
      // limit offset to keep most of image on screen
      let panXMax = (this.currentZoomImage.naturalWidth * this.currentTransform.zoom - this.zoomContainer.clientWidth) / 2.0;
      panXMax = Math.max(panXMax, 0);
      this.currentTransform.panX = Math.min(this.currentTransform.panX, panXMax);
      this.currentTransform.panX = Math.max(this.currentTransform.panX, -panXMax);

      this.currentTransform.panY += y;
      let panYMax = (this.currentZoomImage.naturalHeight * this.currentTransform.zoom - this.zoomContainer.clientHeight) / 2.0;
      panYMax = Math.max(panYMax, 0);
      this.currentTransform.panY = Math.min(this.currentTransform.panY, panYMax);
      this.currentTransform.panY = Math.max(this.currentTransform.panY, -panYMax);
      this.updateImagePosition();
    }

    /**
     * Set current zoom image transform to specific values.
     * @param {number} panX - Pan X value.
     * @param {number} panY - Pan Y value.
     * @param {number} zoom - Current zoom amount.
     */
    setCurrentTransform(panX, panY, zoom) {
      this.currentTransform.panX = panX;
      this.currentTransform.panY = panY;
      this.currentTransform.zoom = zoom;
      this.alterCurrentTransformZoomBy(0);
    }

    /**
     * Update zoom amount by a delta.
     * @param {number} delta - Amount to adjust.
     */
    alterCurrentTransformZoomBy(delta) {
      this.currentTransform.zoom += delta;
      // do not zoom out further than fit
      // Zonder deze uitstap wordt de deling Infinity zodra de foto nog niet gedecodeerd is,
      // en zet Math.max de zoom vervolgens op 1.0 -- ware grootte in plaats van passend.
      if (!this.currentZoomImage.naturalWidth || !this.currentZoomImage.naturalHeight) return;
      const maxZoomX = this.zoomContainer.clientWidth / this.currentZoomImage.naturalWidth;
      const maxZoomY = this.zoomContainer.clientHeight / this.currentZoomImage.naturalHeight;
      this.currentTransform.zoom = Math.max(this.currentTransform.zoom, Math.min(maxZoomX, maxZoomY));

      // do not zoom in further than native size
      this.currentTransform.zoom = Math.min(this.currentTransform.zoom, 1.0);

      // reasses pan bounds
      this.alterCurrentPanBy(0, 0);
      this.updateImagePosition();
    }

    /**
     * Alleen opnieuw passend maken als het fotovlak echt van maat veranderde.
     */
    herpositioneerBijMaatwijziging() {
      if (!this.currentZoomImage) return;
      const b = this.zoomContainer.clientWidth;
      const h = this.zoomContainer.clientHeight;
      if (!b || !h) return;
      if (b === this.vlakMaat.b && h === this.vlakMaat.h) return;
      this.vlakMaat = { b, h };
      this.setInitialImagePosition();
    }

    /**
     * Position the current image in the centre, zoomed out
     *
     * PWS 04-09-2026 -- dit ging op de telefoon mis.
     * De maten kwamen uit clientWidth/clientHeight van de foto. Die kloppen alleen als
     * gallery-zoom.css al is toegepast, en dat bestand wordt met media="print" LUI ingeladen.
     * Was het er nog niet, dan gold de algemene themaregel img{max-width:100%} en was de foto
     * al geschaald; de schaal eronder kwam daar nog eens overheen. Was de foto nog niet
     * gedecodeerd, dan was naturalWidth 0, werd vlakbreedte/0 gelijk aan Infinity en kwam de
     * zoom via Math.max op 1.0 uit: ware grootte, half buiten beeld.
     * Nu: rekenen met naturalWidth/naturalHeight, en niets doen zolang er geen maat is.
     */
    setInitialImagePosition() {
      const foto = this.currentZoomImage;
      if (!foto) return;

      const vlakB = this.zoomContainer.clientWidth;
      const vlakH = this.zoomContainer.clientHeight;
      if (!vlakB || !vlakH || !foto.naturalWidth || !foto.naturalHeight) {
        // Begrensd wachten. Komt er binnen ongeveer twee seconden geen maat, dan is er iets
        // anders mis en is doordraaien op een telefoon erger dan een foto die niet past.
        this.maatPogingen = (this.maatPogingen || 0) + 1;
        if (this.maatPogingen > 120) return;
        if (this.wachtOpMaat) cancelAnimationFrame(this.wachtOpMaat);
        this.wachtOpMaat = requestAnimationFrame(() => {
          this.wachtOpMaat = null;
          this.setInitialImagePosition();
        });
        return;
      }
      this.maatPogingen = 0;

      this.vlakMaat = { b: vlakB, h: vlakH };
      foto.style.top = `${vlakH / 2 - foto.naturalHeight / 2}px`;
      foto.style.left = `${vlakB / 2 - foto.naturalWidth / 2}px`;
      this.setCurrentTransform(0, 0, 0);
    }

    /**
     * Set current zoom image transform based on pan & zoom values.
     */
    updateImagePosition() {
      this.currentZoomImage.style.transform = `translate3d(${this.currentTransform.panX}px, ${this.currentTransform.panY}px, 0) scale(${this.currentTransform.zoom})`;
    }

    /**
     * Haal de zoomfoto van een miniatuur alvast op.
     * @param {Element} thumb - De miniatuur.
     */
    static prefetchThumb(thumb) {
      if (!thumb || thumb.dataset.prefetched || !thumb.dataset.zoomUrl) return;
      thumb.dataset.prefetched = '1';
      const img = new Image();
      img.src = thumb.dataset.zoomUrl;
    }

    /**
     * Haal de foto links en rechts van de huidige alvast op.
     * @param {Element} thumb - De huidige miniatuur.
     */
    prefetchBuren(thumb) {
      const zichtbaar = [...this.thumbContainer.children].filter((el) => !el.hidden);
      const i = zichtbaar.indexOf(thumb);
      if (i === -1) return;
      GalleryZoom.prefetchThumb(zichtbaar[i - 1]);
      GalleryZoom.prefetchThumb(zichtbaar[i + 1]);
    }

    /**
     * Select a thumbnail.
     * @param {Element} thumb - Thumbnail to select.
     */
    selectThumb(thumb) {
      let activeThumb;

      [...thumb.parentElement.children].forEach((el) => {
        if (el === thumb) {
          activeThumb = thumb;
          el.classList.add('gallery-zoom__thumb--active');
        } else {
          el.classList.remove('gallery-zoom__thumb--active');
        }
      });

      const visibleThumbs = Array.from(activeThumb.parentElement.children).filter((child) => !child.hidden);
      const activeThumbIndex = visibleThumbs.indexOf(activeThumb);
      if (this.counterCurrent) this.counterCurrent.textContent = activeThumbIndex + 1;

      // If the thumb isn't visible, scroll to it
      this.scrollToThumb(activeThumb);

      this.previousBtn.disabled = activeThumbIndex === 0;
      this.nextBtn.disabled = (activeThumbIndex === (visibleThumbs.length - 1));

      // replace zoom image
      this.zoomContainer.classList.add('gallery-zoom__zoom-container--loading');
      this.currentZoomImage = GalleryZoom.createEl('img', 'gallery-zoom__zoom-image');
      // Deze maten staan ook in gallery-zoom.css, maar dat blad komt met media="print" pas
      // na het renderen binnen. Inline gezet zijn ze er meteen, en dan is naturalWidth ook
      // werkelijk de breedte waarmee hierboven wordt gerekend.
      Object.assign(this.currentZoomImage.style, {
        position: 'absolute', width: 'auto', height: 'auto',
        maxWidth: 'none', maxHeight: 'none', minWidth: '0', minHeight: '0'
      });
      this.currentZoomImage.alt = thumb.querySelector('.gallery-zoom__thumb-img')?.alt;
      this.currentZoomImage.style.visibility = 'hidden';
      this.currentZoomImage.onload = () => {
        this.zoomContainer.classList.remove('gallery-zoom__zoom-container--loading');
        this.currentZoomImage.style.visibility = '';
        // De grote foto wordt pas na de klik opgehaald en dat kost op een koude CDN-rand
        // een halve seconde. De buurfoto's alvast halen maakt bladeren onmiddellijk.
        this.prefetchBuren(thumb);
        this.setInitialImagePosition();
      };
      this.currentZoomImage.src = thumb.dataset.zoomUrl;
      this.zoomContainer.replaceChildren(this.currentZoomImage);
    }

    /**
     * Scrolls the thumbnail slider to the active thumbnail if necessary
     * @param {Element} thumb - Thumbnail to scroll to.
     */
    scrollToThumb(thumb) {
      const thumbSliderRect = this.thumbSlider.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();

      const thumbLeft = thumbRect.left - thumbSliderRect.left;
      const thumbRight = thumbRect.right - thumbSliderRect.right;

      if (thumbLeft < 0) {
        this.thumbSlider.scrollLeft += thumbLeft - (this.thumbSlider.clientWidth - thumbRect.width);
      } else if (thumbRight > 0) {
        this.thumbSlider.scrollLeft += thumbRight + (this.thumbSlider.clientWidth - thumbRect.width);
      }
    }

    /**
     * Select previous thumbnail.
     * @param {Event} evt - Click event, if triggered by pagination.
     */
    selectPreviousThumb(evt) {
      if (evt) evt.preventDefault();
      if (this.thumbContainer.childElementCount < 2) return;

      let previous = this.thumbContainer.querySelector('.gallery-zoom__thumb--active').previousElementSibling;
      while (!previous || !previous.offsetParent) {
        if (!previous) {
          previous = this.thumbContainer.lastElementChild;
        } else {
          previous = previous.previousElementSibling;
        }
      }
      this.selectThumb(previous);
    }

    /**
     * Select next thumbnail.
     * @param {Event} evt - Click event, if triggered by pagination.
     */
    selectNextThumb(evt) {
      if (evt) evt.preventDefault();
      if (this.thumbContainer.childElementCount < 2) return;

      let next = this.thumbContainer.querySelector('.gallery-zoom__thumb--active').nextElementSibling;
      while (!next || !next.offsetParent) {
        if (!next) {
          next = this.thumbContainer.firstElementChild;
        } else {
          next = next.nextElementSibling;
        }
      }
      this.selectThumb(next);
    }

    /**
     * Call to stop tracking touch events.
     */
    stopTrackingTouch() {
      this.pinchTracking.isTracking = false;
      this.touchTracking.isTracking = false;
    }

    /**
     * Handle mouse and touch events.
     * @param {object} evt - Event object.
     */
    trackInputMovement(evt) {
      evt.preventDefault();
      if (evt.type === 'touchmove' && evt.touches.length > 0) {
        // pan — alleen met een vinger. Met twee vingers is dit knijpen, en dan schoof de
        // foto tegelijk weg omdat dit blok op touches[0] bleef rekenen.
        const touch1 = evt.touches[0];
        if (evt.touches.length === 1) {
          if (!this.touchTracking.isTracking) {
            this.touchTracking.isTracking = true;
            this.touchTracking.lastTouchX = touch1.clientX;
            this.touchTracking.lastTouchY = touch1.clientY;
          } else {
            this.alterCurrentPanBy(
              (touch1.clientX - this.touchTracking.lastTouchX) * this.touchPanModifier,
              (touch1.clientY - this.touchTracking.lastTouchY) * this.touchPanModifier
            );
            this.touchTracking.lastTouchX = touch1.clientX;
            this.touchTracking.lastTouchY = touch1.clientY;
          }
        } else {
          this.touchTracking.isTracking = false;
        }

        if (evt.touches.length === 2) {
          // pinch
          const touch2 = evt.touches[1];
          const pinchDistance = Math.sqrt((touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2);
          if (!this.pinchTracking.isTracking) {
            this.pinchTracking.lastPinchDistance = pinchDistance;
            this.pinchTracking.isTracking = true;
          } else {
            const pinchDelta = pinchDistance - this.pinchTracking.lastPinchDistance;
            this.alterCurrentTransformZoomBy(pinchDelta * this.pinchZoomMultiplier);
            this.pinchTracking.lastPinchDistance = pinchDistance;
          }
        } else {
          this.pinchTracking.isTracking = false;
        }
      } else {
        // mousemove
        this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
      }
    }

    /**
     * Handle mouse wheel scroll - use for zoom.
     * @param {object} evt - Event object.
     */
    trackWheel(evt) {
      evt.preventDefault();
      if (evt.deltaY !== 0) {
        this.alterCurrentTransformZoomBy(evt.deltaY * this.wheelZoomMultiplier);
      }
    }

    /**
     * Handle click on a thumbnail image.
     * @param {object} evt - Event object.
     */
    onThumbClick(evt) {
      evt.preventDefault();
      this.selectThumb(evt.currentTarget);
    }

    /**
     * Handle click on the main zoom image. (Toggle zoom in/out.)
     * @param {object} evt - Event object.
     */
    onZoomContainerClick(evt) {
      evt.preventDefault();

      if (this.currentTransform.zoom === 1.0) {
        this.currentTransform.zoom = 0;
        this.alterCurrentTransformZoomBy(0);
      } else {
        this.currentTransform.zoom = 1;
        this.alterCurrentTransformZoomBy(0);
        this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
      }
    }

    /**
     * Handle key events.
     * @param {object} evt - Event object.
     */
    handleKeyup(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
          evt.preventDefault();
          this.selectPreviousThumb();
          break;
        case 'ArrowRight':
          evt.preventDefault();
          this.selectNextThumb();
          break;
      }
    }
  }

  window.customElements.define('gallery-zoom', GalleryZoom);
}
